import CustomTooltip from '../../../shared/CustomTooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Add,
    CheckCircle,
    Close,
    HelpOutline,
    NavigateBefore,
    NavigateNext,
    Refresh,
} from '@mui/icons-material';
import EngineeringIcon from '@mui/icons-material/Engineering';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import {
    Alert,
    Avatar,
    Box,
    Button,
    ButtonGroup,
    Card,
    CardContent,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    ListItemAvatar,
    ListItemText,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { lighten } from '@mui/material/styles';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import { AddToCalendarButton } from 'add-to-calendar-button-react';
import { isEqual } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import {
    FormProvider,
    useForm,
    useFormContext,
    useWatch,
} from 'react-hook-form';
import { useLocation, useSearchParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import {
    useAcceptJobMutation,
    useAddContractorNotesMutation,
    useAddJobCompletionMutation,
    useAddJobPhotosMutation,
    useCancelJobMutation,
    useCompleteJobMutation,
    useEditJobMutation,
    useGetIndividualContractorQuery,
    useGetIndividualJobQuery,
    useGetNirovisionJobAccessListQuery,
    useGetOwnerContractorsQuery,
    useGetPricingTemplatesQuery,
    useGetUserListQuery,
    useProvideNirovisionContractorAccessMutation,
    useReassignJobMutation,
    useRemoveNirovisionContractorAccessMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import {
    Job,
    JobCompletion,
    JobItem,
    ProviderType,
} from '../../../../app/Slices/SevenDayApiTypes';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import UserRole from '../../../../shared/AuthRoles';
import {
    camelCaseToTitleCase,
    formatCurrency,
    formatTime,
    handleHttpError,
    sum,
} from '../../../../shared/utils';
import { Status } from '../../../enums/contractor';
import { ContractorRole, OwnerRole } from '../../../enums/users';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import FormDateFilter from '../../../shared/FormDateFilter';
import FormMultiAutocomplete from '../../../shared/FormMultiAutocomplete';
import FormTextField from '../../../shared/FormTextField';
import FormUpload from '../../../shared/FormUpload';
import toast from '../../../shared/Toast';
import { ModalProps } from '../../../types/modal';
import NirovisionAccessTable from '../../Nirovision/NirovisionAccessTable';
import { getUnavailableDays } from '../../Profile/Form/Contractor/Availability';
import {
    getDisabledDateMessage,
    shouldDisableDate,
} from '../../Reservations/ReservationContractors/ContractorModal';
import { groupJobItemsByTemplate } from '../../Reservations/ReservationRequest/ReservationJobs/ReservationJobForm';
import ContactForm from './ContactForm';
import DeclineModal from './DeclineModal';
import MarkAsCompletedModal from './MarkAsCompletedModal';
import { theme } from '../../../../shared/theme';
import Lightbox from 'yet-another-react-lightbox';
import { Media } from '../../../types/user';
import { getAvailableTimes } from '../utils/availability.utils';
import {
    getContractorBusinessDayNumbers,
    getEffectiveJobRequestCutoffDays,
} from '../utils/cutoff.utils';

interface JobPreviewProps extends ModalProps {
    id: number | null;
    jobIds?: { id: number; providerType: ProviderType }[];
    initialIndex?: number;
    onNavigate?: (nextIndex: number) => void;
}

const Schema = z.object({
    ownerNotes: z.string(),
    jobDate: z.coerce.date(),
    startTime: z.string().optional().nullable(),
    endTime: z.string().optional().nullable(),
    contractorNotes: z.string(),
    services: z.array(z.string()),
    jobCompletion: z.object({
        notes: z.string(),
        beforePhotos: z.array(z.object({ id: z.string(), src: z.string() })),
        afterPhotos: z.array(z.object({ id: z.string(), src: z.string() })),
    }),
    contractor: z.object({ id: z.number() }),
    assignee: z
        .object({ id: z.number({ message: 'Please select an assignee' }) })
        .nullable(),
});

type FormValues = z.infer<typeof Schema>;

export const FieldLabel = ({
    children,
    htmlFor,
}: {
    children: React.ReactNode;
    htmlFor?: string;
}) => (
    <InputLabel
        htmlFor={htmlFor}
        sx={{
            fontSize: '12px',
            lineHeight: 1.2,
            fontWeight: 700,
        }}
    >
        {children}
    </InputLabel>
);

export const FormField = ({
    label,
    children,
    fieldId,
}: {
    label: React.ReactNode;
    children: React.ReactNode;
    fieldId?: string;
}) => (
    <Box>
        <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>
        {children}
    </Box>
);

const ActionButtons = ({
    onCancel,
    saveDisabled,
}: {
    onCancel: () => void;
    saveDisabled?: boolean;
}) => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant='outlined' color='warning' onClick={onCancel}>
            Cancel
        </Button>
        <Button
            variant='contained'
            color='primary'
            type='submit'
            disabled={saveDisabled}
        >
            Save
        </Button>
    </Box>
);

export const NavigationControls = ({
    atStart,
    atEnd,
    onPrev,
    onNext,
    showLabels = true,
    compact = false,
}: {
    atStart: boolean;
    atEnd: boolean;
    onPrev: () => void;
    onNext: () => void;
    showLabels?: boolean;
    compact?: boolean;
}) => (
    <ButtonGroup
        size='small'
        variant='outlined'
        sx={
            compact
                ? {
                      height: 34,
                      flexShrink: 0,
                      '& .MuiButton-root': { minWidth: 44, px: 1 },
                  }
                : undefined
        }
    >
        <Button
            startIcon={<NavigateBefore />}
            onClick={onPrev}
            disabled={atStart}
        >
            {showLabels ? 'Prev' : null}
        </Button>
        <Button endIcon={<NavigateNext />} onClick={onNext} disabled={atEnd}>
            {showLabels ? 'Next' : null}
        </Button>
    </ButtonGroup>
);

export const getStatusSx = (s: Status | string) => {
    switch (s) {
        case Status.Accepted:
        case Status.PendingCompletion:
        case Status.Active:
        case 'approved':
            return {
                bgcolor: lighten(theme.palette.yellow.main, 0.78),
                color: theme.palette.dark.main,
                borderColor: lighten(theme.palette.yellow.main, 0.35),
            };
        case Status.Pending:
        case 'pending':
            return {
                bgcolor: lighten(theme.palette.primary.main, 0.76),
                color: theme.palette.dark.main,
                borderColor: lighten(theme.palette.primary.main, 0.32),
            };
        case Status.Cancelled:
        case Status.Declined:
        case 'cancelled':
        case 'declined':
            return {
                bgcolor: lighten(theme.palette.error.main, 0.82),
                color: theme.palette.dark.main,
                borderColor: lighten(theme.palette.error.main, 0.3),
            };
        case Status.Completed:
        case 'completed':
            return {
                bgcolor: lighten(theme.palette.info.main, 0.82),
                color: theme.palette.dark.main,
                borderColor: lighten(theme.palette.info.main, 0.3),
            };
        default:
            return {
                bgcolor: theme.palette.custom.cardActionBackground,
                color: theme.palette.text.primary,
                borderColor: theme.palette.custom.lightBorderColor,
            };
    }
};

const DEFAULT_MAP_CENTER = { lat: -33.865143, lng: 151.2099 };

const JobMap = ({ experience }: { experience: Job['module'] }) => {
    const [marker, setMarker] = useState<google.maps.LatLngLiteral | null>(
        null,
    );

    const address = `${experience?.streetAddress}, ${experience?.suburb}, ${experience?.state}`;

    const fetchMarker = async () => {
        if (!window.google) return;

        const { Geocoder } = (await google.maps.importLibrary(
            'geocoding',
        )) as google.maps.GeocodingLibrary;

        const geocoder = new Geocoder();

        geocoder
            .geocode({ address })
            .then(({ results }) => {
                const location = results[0].geometry.location;
                setMarker({ lat: location.lat(), lng: location.lng() });
            })
            .catch(() => {});
    };

    useEffect(() => {
        if (experience) fetchMarker();
    }, [experience]);

    const center = marker ?? DEFAULT_MAP_CENTER;

    return (
        <Map
            disableDefaultUI
            center={center}
            zoom={14}
            style={{ height: 150 }}
            clickableIcons={false}
            onClick={() =>
                window.open(
                    `https://google.com/maps/place/${address}`,
                    '_blank',
                )
            }
            draggableCursor='pointer'
        >
            {marker && <Marker position={marker} />}
        </Map>
    );
};

const JobMetaCard = ({ job, address }: { job: Job; address: string }) => (
    <Card variant='outlined'>
        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
            <JobMap experience={job.module} />
        </APIProvider>

        <CardContent>
            <Stack spacing={1.5}>
                <Box>
                    <Typography
                        variant='body3'
                        component='span'
                        sx={{ lineHeight: 1.2, fontWeight: 700 }}
                    >
                        Address:
                    </Typography>
                    <Typography
                        variant='body3'
                        component='span'
                        sx={{ ml: 0.5 }}
                    >
                        {address}
                    </Typography>
                </Box>

                {job.booking.guest && (
                    <>
                        {job.booking.guest.guests && (
                            <Box>
                                <Typography
                                    variant='body3'
                                    component='span'
                                    sx={{ lineHeight: 1.2, fontWeight: 700 }}
                                >
                                    No. of Guests:
                                </Typography>
                                <Typography
                                    variant='body3'
                                    component='span'
                                    sx={{ ml: 0.5 }}
                                >
                                    {job.booking.guest.guests} for Reservation #
                                    {job.booking.id}
                                </Typography>
                            </Box>
                        )}

                        <Box>
                            <Typography
                                variant='body3'
                                component='span'
                                sx={{ lineHeight: 1.2, fontWeight: 700 }}
                            >
                                Check-in:
                            </Typography>
                            <Typography
                                variant='body3'
                                component='span'
                                sx={{ ml: 0.5 }}
                            >
                                {new Date(
                                    job.booking.startDate,
                                ).toLocaleDateString('en-AU')}{' '}
                                {formatTime(job.booking.guest.checkinTime)}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography
                                variant='body3'
                                component='span'
                                sx={{ lineHeight: 1.2, fontWeight: 700 }}
                            >
                                Checkout:
                            </Typography>
                            <Typography
                                variant='body3'
                                component='span'
                                sx={{ ml: 0.5 }}
                            >
                                {new Date(
                                    job.booking.endDate,
                                ).toLocaleDateString('en-AU')}{' '}
                                {formatTime(job.booking.guest.checkoutTime)}
                            </Typography>
                        </Box>
                    </>
                )}
            </Stack>
        </CardContent>
    </Card>
);

const ContractorJobPreview = ({
    id,
    open,
    onClose,
    jobIds = [],
    initialIndex = 0,
    onNavigate,
}: JobPreviewProps) => {
    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const location = useLocation();
    const navigate = useNavigate();

    const isOwnerAdmin = loggedInUser?.role === OwnerRole.Admin;
    const isContractor = loggedInUser?.role.includes('contractor');
    const isContractorAdmin = loggedInUser?.role === ContractorRole.Admin;

    const [declineOpen, setDeclineOpen] = useState(false);
    const [tabPanel, setTabPanel] = useState(0);

    const [searchParams] = useSearchParams();
    const providerType = searchParams.get('provider-type');
    const currentJobId = Number(searchParams.get('selectedJob'));

    const currentIndex = jobIds.findIndex(
        (job) =>
            job.id === currentJobId &&
            (providerType ? job.providerType === providerType : true),
    );

    const atStart = jobIds.length > 0 ? currentIndex <= 0 : true;
    const atEnd = jobIds.length > 0 ? currentIndex >= jobIds.length - 1 : true;

    const [cancelJob] = useCancelJobMutation();
    const [acceptJob] = useAcceptJobMutation();
    const [reassignJob] = useReassignJobMutation();
    const [updateJob] = useEditJobMutation();
    const [addNotes] = useAddContractorNotesMutation();
    const [addCompletion] = useAddJobCompletionMutation();

    const methods = useForm<FormValues>({
        resolver: zodResolver(Schema),
        mode: 'onChange',
    });

    const {
        control,
        reset,
        handleSubmit,
        setValue,
        formState: { dirtyFields, isSubmitting, isDirty },
    } = methods;

    const go = (delta: number) => {
        if (jobIds.length === 0) return;
        const nextIndex = currentIndex + delta;
        if (nextIndex < 0 || nextIndex >= jobIds.length) return;
        onNavigate?.(nextIndex);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowRight' && !atEnd) go(1);
        if (e.key === 'ArrowLeft' && !atStart) go(-1);
    };

    const { data: job, isLoading } = useGetIndividualJobQuery(
        currentJobId as number,
        {
            skip: !currentJobId,
        },
    );

    const { data: contractor } = useGetIndividualContractorQuery(
        job?.contractor.id as number,
        {
            skip: !job?.contractor,
        },
    );

    const { data: contractors } = useGetOwnerContractorsQuery(
        loggedInUser?.company,
        {
            skip: loggedInUser?.role !== OwnerRole.Admin,
        },
    );

    const contractorOptions = isContractor
        ? contractor
            ? [{ value: contractor.id, label: contractor.name }]
            : []
        : (contractors?.map(({ id: cid, name }) => ({
              value: cid,
              label: name,
          })) ?? []);

    const { data: contractorUsers } = useGetUserListQuery(
        `/contractors/${loggedInUser?.company}/users`,
        { skip: loggedInUser?.role !== ContractorRole.Admin },
    );

    const assigneeLabel = ({
        email,
        firstName,
        lastName,
    }: {
        email: string;
        firstName: string;
        lastName: string;
    }) => (firstName ? `${firstName} ${lastName} (${email})` : email);

    const assigneeOptions:
        | {
              value: string;
              label: string;
              email: string;
              fullName: string;
              profilePicture?: { src: string };
          }[]
        | [] = contractorUsers
        ? contractorUsers.map(
              ({
                  id: uid,
                  email,
                  firstName,
                  lastName,
                  profilePicture,
              }: {
                  id: number;
                  email: string;
                  firstName: string;
                  lastName: string;
                  profilePicture?: { src: string };
              }) => ({
                  value: uid as any,
                  label: assigneeLabel({ email, firstName, lastName }),
                  fullName: firstName ? `${firstName} ${lastName}` : '',
                  email,
                  profilePicture,
              }),
          )
        : job?.assignee
          ? [
                {
                    value: job.assignee.id as any,
                    label: assigneeLabel(job.assignee),
                    fullName: job.assignee.firstName
                        ? `${job.assignee.firstName} ${job.assignee.lastName}`
                        : '',
                    email: job.assignee.email,
                },
            ]
          : [];

    const selectedContractor = useWatch({ name: 'contractor', control }) ?? {
        id: undefined,
    };
    const selectedDate = useWatch({ name: 'jobDate', control });
    const startTime = useWatch({ name: 'startTime', control });
    const endTime = useWatch({ name: 'endTime', control });

    const services = isContractor
        ? contractor?.services
        : contractors?.find(({ id: cid }) => cid === selectedContractor?.id)
              ?.services;

    useEffect(() => {
        if (job) reset(job as any);
    }, [job, reset]);

    const handleViewRecurringJob = () => {
        if (!job?.recurringId) return;
        navigate(`/recurringJobs?selectedJob=${job.recurringId}`);
    };

    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxPhotos, setLightboxPhotos] = useState<Media[]>([]);
    const [selectedPhoto, setSelectedPhoto] = useState<number>();
    const [notesFormOpen, setNotesFormOpen] = useState(false);

    const address = `${job?.module.streetAddress}, ${job?.module.suburb}, ${job?.module.state}`;

    const handleClose = () => {
        setNotesFormOpen(false);
        onClose();
    };

    const handleCancel = async () => {
        if (!job) return;
        try {
            await cancelJob(job.id).unwrap();
            onClose();
            toast.success('Job cancelled successfully');
        } catch {
            toast.error('Error cancelling job');
        }
    };

    const handleAccept = async (data: FormValues) => {
        if (!job) return;
        try {
            await acceptJob({
                id: job.id,
                assignee: data.assignee ?? { id: loggedInUser.sub },
            }).unwrap();
            toast.success('Job accepted successfully');
        } catch {
            toast.error('Error accepting job');
        }
    };

    const handleReassign = async (data: FormValues) => {
        if (!job || !data.assignee) return;
        try {
            await reassignJob({
                id: job.id,
                assignee: data.assignee,
            }).unwrap();
            toast.success('Job updated successfully');
        } catch {
            toast.error('Error updating job');
        }
    };

    const calendarDescription = ` Job #${job?.id} for 7DAY Booking ID#${job?.booking.id} [br] [li] Job Date: ${
        job?.jobDate ? new Date(job.jobDate).toLocaleDateString('en-AU') : ''
    } [br] [li] Services Required: ${job?.services}
     [br] [li] No. of Guests ${job?.booking?.guest?.guests}
      [br] [li] Guest checkin and out ${
          job?.booking.startDate
              ? new Date(job.booking.startDate).toLocaleDateString('en-AU')
              : ''
      } at ${job?.booking?.guest?.checkinTime} - ${
          job?.booking.endDate
              ? new Date(job.booking.endDate).toLocaleDateString('en-AU')
              : ''
      } at ${job?.booking?.guest?.checkoutTime}
       [br] [li] Requested by  ${job?.owner.name} `;

    const onSubmit = async (data: FormValues) => {
        try {
            if (!currentJobId) return;

            if (isContractor) {
                if (dirtyFields.contractorNotes) {
                    await addNotes({
                        id: currentJobId,
                        ...data,
                    } as any).unwrap();
                    toast.success('Notes saved successfully');
                    setNotesFormOpen(false);
                }
                if (dirtyFields.jobCompletion) {
                    await addCompletion([
                        currentJobId,
                        data.jobCompletion,
                    ] as any).unwrap();
                    toast.success('Progress notes saved successfully');
                }
            }

            if (isOwnerAdmin) {
                const { contractorNotes, ...rest } = data as any;
                const request = {
                    ...rest,
                    jobDate: rest.jobDate
                        ? new Date(rest.jobDate as any)
                              .toISOString()
                              .slice(0, 10)
                        : undefined,
                    contractor:
                        selectedContractor?.id &&
                        selectedContractor.id !== contractor?.id
                            ? data.contractor
                            : undefined,
                };
                await updateJob([currentJobId, request] as any).unwrap();
                toast.success('Job updated successfully');
                handleClose();
            }
        } catch {
            toast.error('Error updating job');
        }
    };

    const unavailablePeriods =
        contractors?.find(({ id: cid }) => cid === selectedContractor?.id)
            ?.unavailablePeriods || [];
    const availableDays =
        contractors?.find(({ id: cid }) => cid === selectedContractor?.id)
            ?.availableDays || [];
    const unavailableDays = getUnavailableDays(
        availableDays.map((availability) => availability.day),
    );
    const disabledDays = { unavailablePeriods, unavailableDays };

    const { data: pricingTemplates } = useGetPricingTemplatesQuery(
        selectedContractor?.id as number,
        { skip: !selectedContractor?.id || isContractor },
    );

    const activeContractor = contractors?.find(
        ({ id: cid }) => cid === selectedContractor?.id,
    );
    const businessDayNumbers = getContractorBusinessDayNumbers(availableDays);
    const effectiveCutoffDays = getEffectiveJobRequestCutoffDays({
        contractorCutoff: activeContractor?.cutoff,
        templates: pricingTemplates ?? [],
        items: (job?.items as JobItem[]) ?? [],
    });

    const groupedJobItems = groupJobItemsByTemplate((job?.items as any) || []);

    const chipSx = job ? getStatusSx(job.status) : getStatusSx('default');

    const { startTimes, endTimes } = getAvailableTimes(
        contractor,
        selectedDate,
        startTime,
        endTime,
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullScreen={isMobile}
                maxWidth='lg'
                fullWidth
                sx={{
                    '& .MuiDialog-container': {
                        alignItems: isMobile ? 'stretch' : 'center',
                    },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 3,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                    },
                }}
                onKeyDown={handleKeyDown}
            >
                {isLoading ? (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 3,
                        }}
                    >
                        <LoadingComponent />
                    </Box>
                ) : (
                    <FormProvider {...methods}>
                        <form
                            onSubmit={handleSubmit(onSubmit, () =>
                                toast.error(
                                    'Please fix the highlighted fields before saving.',
                                ),
                            )}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                minHeight: 0,
                            }}
                        >
                            {job && (
                                <>
                                    <DialogTitle>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <EngineeringIcon
                                                    sx={{ opacity: 0.55 }}
                                                    fontSize='small'
                                                />
                                                Job Request #{job.id}
                                            </Box>
                                            <CustomChip
                                                variant='outlined'
                                                label={camelCaseToTitleCase(
                                                    job.status,
                                                ).toUpperCase()}
                                                sx={{
                                                    ...chipSx,
                                                    '& .MuiChip-label': {
                                                        overflow: 'hidden',
                                                        textOverflow:
                                                            'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    },
                                                }}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            {jobIds.length > 1 &&
                                                (!isMobile ? (
                                                    <NavigationControls
                                                        atStart={atStart}
                                                        atEnd={atEnd}
                                                        onPrev={() => go(-1)}
                                                        onNext={() => go(1)}
                                                    />
                                                ) : (
                                                    <NavigationControls
                                                        atStart={atStart}
                                                        atEnd={atEnd}
                                                        onPrev={() => go(-1)}
                                                        onNext={() => go(1)}
                                                        showLabels={false}
                                                        compact
                                                    />
                                                ))}

                                            <IconButton
                                                onClick={handleClose}
                                                sx={{
                                                    color: 'text.primary',
                                                }}
                                                aria-label='close'
                                            >
                                                <Close />
                                            </IconButton>
                                        </Box>
                                    </DialogTitle>
                                    <DialogContent>
                                        {job.status === Status.Completed &&
                                            job.completedAt &&
                                            job.markedCompleteBy && (
                                                <Alert sx={{ mb: 2 }}>
                                                    {`This job was marked complete
                                                    on ${new Date(
                                                        job.completedAt,
                                                    ).toLocaleDateString(
                                                        'en-AU',
                                                    )} by ${job.markedCompleteBy.companyName}`}
                                                </Alert>
                                            )}
                                        {(job.status ===
                                            Status.PendingCompletion ||
                                            job.status === Status.Accepted) && (
                                            <CompleteJobAlert id={job.id} />
                                        )}
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={8}>
                                                <Stack spacing={1}>
                                                    <FormField label='Contractor'>
                                                        <FormAutocomplete
                                                            name='contractor.id'
                                                            control={control}
                                                            options={
                                                                contractorOptions
                                                            }
                                                            disabled={
                                                                isContractor ||
                                                                job.status ===
                                                                    Status.Completed
                                                            }
                                                            onChange={(
                                                                _,
                                                                data,
                                                            ) => {
                                                                if (!data)
                                                                    return;
                                                                setValue(
                                                                    'contractor.id',
                                                                    data.value as any,
                                                                );
                                                                setValue(
                                                                    'services',
                                                                    [],
                                                                );
                                                            }}
                                                        />
                                                    </FormField>

                                                    <FormField label='Requested By'>
                                                        <FormTextField
                                                            name='owner.name'
                                                            control={control}
                                                            disabled
                                                        />
                                                    </FormField>

                                                    {isContractor && (
                                                        <FormField label='Assignee'>
                                                            <FormAutocomplete
                                                                PaperComponent={({
                                                                    children,
                                                                }) => (
                                                                    <Paper>
                                                                        <Button
                                                                            fullWidth
                                                                            size='large'
                                                                            sx={{
                                                                                justifyContent:
                                                                                    'flex-start',
                                                                                pl: 2,
                                                                                mt: 1,
                                                                            }}
                                                                            startIcon={
                                                                                <Add />
                                                                            }
                                                                            onMouseDown={(
                                                                                e,
                                                                            ) => {
                                                                                e.stopPropagation();
                                                                                window.open(
                                                                                    '/newUser',
                                                                                    '_blank',
                                                                                );
                                                                            }}
                                                                        >
                                                                            Create
                                                                            New
                                                                            User
                                                                        </Button>
                                                                        {
                                                                            children
                                                                        }
                                                                    </Paper>
                                                                )}
                                                                name='assignee.id'
                                                                control={
                                                                    control
                                                                }
                                                                options={
                                                                    assigneeOptions
                                                                }
                                                                disabled={
                                                                    !isContractorAdmin
                                                                }
                                                                renderOption={(
                                                                    props,
                                                                    option: any,
                                                                ) => (
                                                                    <li
                                                                        key={
                                                                            option.value
                                                                        }
                                                                        {...props}
                                                                    >
                                                                        <ListItemAvatar>
                                                                            <Avatar
                                                                                variant='circular'
                                                                                src={
                                                                                    option
                                                                                        .profilePicture
                                                                                        ?.src
                                                                                }
                                                                            />
                                                                        </ListItemAvatar>
                                                                        <ListItemText
                                                                            primary={
                                                                                option.fullName
                                                                            }
                                                                            secondary={
                                                                                option.email
                                                                            }
                                                                        />
                                                                    </li>
                                                                )}
                                                            />
                                                        </FormField>
                                                    )}

                                                    <FormField
                                                        label='Date'
                                                        fieldId='contractor-job-date'
                                                    >
                                                        <FormDateFilter
                                                            id='contractor-job-date'
                                                            name='jobDate'
                                                            control={control}
                                                            disabled={
                                                                isContractor
                                                            }
                                                            shouldDisableDate={(
                                                                date,
                                                            ) =>
                                                                shouldDisableDate(
                                                                    date,
                                                                    disabledDays,
                                                                    effectiveCutoffDays,
                                                                    businessDayNumbers,
                                                                )
                                                            }
                                                            getDisabledDateMessage={(
                                                                date,
                                                            ) =>
                                                                getDisabledDateMessage(
                                                                    date,
                                                                    disabledDays,
                                                                    effectiveCutoffDays,
                                                                    businessDayNumbers,
                                                                )
                                                            }
                                                        />
                                                    </FormField>

                                                    <FormField
                                                        label='Start Time'
                                                        fieldId='contractor-job-start-time'
                                                    >
                                                        <FormAutocomplete
                                                            id='contractor-job-start-time'
                                                            name='startTime'
                                                            control={control}
                                                            options={startTimes}
                                                            disabled={
                                                                isContractor
                                                            }
                                                        />
                                                    </FormField>

                                                    <FormField
                                                        label='End Time'
                                                        fieldId='contractor-job-end-time'
                                                    >
                                                        <FormAutocomplete
                                                            id='contractor-job-end-time'
                                                            name='endTime'
                                                            control={control}
                                                            options={endTimes}
                                                            disabled={
                                                                isContractor
                                                            }
                                                        />
                                                    </FormField>

                                                    <FormField label='Services'>
                                                        <FormMultiAutocomplete
                                                            name='services'
                                                            control={control}
                                                            options={
                                                                services?.items?.map(
                                                                    (
                                                                        item: string,
                                                                    ) => ({
                                                                        label: camelCaseToTitleCase(
                                                                            item,
                                                                        ),
                                                                        value: item,
                                                                    }),
                                                                ) || []
                                                            }
                                                            disabled={
                                                                isContractor
                                                            }
                                                        />
                                                    </FormField>

                                                    <FormField
                                                        label='Job Description'
                                                        fieldId='contractor-job-description'
                                                    >
                                                        <FormTextField
                                                            id='contractor-job-description'
                                                            name='ownerNotes'
                                                            control={control}
                                                            size='small'
                                                            fullWidth
                                                            multiline
                                                            rows={8}
                                                            disabled={
                                                                isContractor
                                                            }
                                                        />
                                                    </FormField>

                                                    {(groupedJobItems.length >
                                                        0 ||
                                                        job.guestNotes) && (
                                                        <Box>
                                                            <Typography
                                                                sx={{
                                                                    fontWeight:
                                                                        'bold',
                                                                    marginBottom: 0.5,
                                                                    fontSize:
                                                                        '16px',
                                                                }}
                                                            >
                                                                Selected Items
                                                            </Typography>

                                                            <Stack
                                                                sx={{
                                                                    gap: 0.5,
                                                                }}
                                                            >
                                                                {groupedJobItems.length >
                                                                    0 &&
                                                                    groupedJobItems.map(
                                                                        (
                                                                            item: any,
                                                                        ) => (
                                                                            <Box
                                                                                key={
                                                                                    item
                                                                                        .template
                                                                                        .id
                                                                                }
                                                                            >
                                                                                <Typography
                                                                                    sx={{
                                                                                        pt: 1,
                                                                                        pb: 0.5,
                                                                                        fontWeight:
                                                                                            'bold',
                                                                                        fontSize:
                                                                                            '14px',
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        item
                                                                                            .template
                                                                                            .name
                                                                                    }{' '}
                                                                                    Menu
                                                                                </Typography>

                                                                                <Box
                                                                                    sx={{
                                                                                        display:
                                                                                            'flex',
                                                                                        flexDirection:
                                                                                            'column',
                                                                                        gap: 1,
                                                                                    }}
                                                                                >
                                                                                    {item.jobItems.map(
                                                                                        (
                                                                                            ji: any,
                                                                                            itemIndex: number,
                                                                                        ) => (
                                                                                            <Grid
                                                                                                key={
                                                                                                    itemIndex
                                                                                                }
                                                                                                sx={{
                                                                                                    display:
                                                                                                        'flex',
                                                                                                    justifyContent:
                                                                                                        'space-between',
                                                                                                }}
                                                                                            >
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        flex: 1,
                                                                                                    }}
                                                                                                >
                                                                                                    <Typography
                                                                                                        sx={{
                                                                                                            fontSize:
                                                                                                                '14px',
                                                                                                        }}
                                                                                                    >
                                                                                                        {
                                                                                                            ji.name
                                                                                                        }
                                                                                                    </Typography>

                                                                                                    {ji.description && (
                                                                                                        <Typography
                                                                                                            sx={{
                                                                                                                fontStyle:
                                                                                                                    'italic',
                                                                                                                fontSize:
                                                                                                                    '14px',
                                                                                                            }}
                                                                                                        >
                                                                                                            {
                                                                                                                ji.description
                                                                                                            }
                                                                                                        </Typography>
                                                                                                    )}

                                                                                                    <Typography
                                                                                                        sx={{
                                                                                                            fontSize:
                                                                                                                '14px',
                                                                                                        }}
                                                                                                    >
                                                                                                        {
                                                                                                            ji.quantity
                                                                                                        }{' '}
                                                                                                        ×{' '}
                                                                                                        {`${formatCurrency(ji.cost)} (ex. GST)`}
                                                                                                    </Typography>
                                                                                                </Box>

                                                                                                <Typography
                                                                                                    sx={{
                                                                                                        fontSize:
                                                                                                            '14px',
                                                                                                    }}
                                                                                                >
                                                                                                    {formatCurrency(
                                                                                                        ji.total,
                                                                                                    )}
                                                                                                </Typography>
                                                                                            </Grid>
                                                                                        ),
                                                                                    )}
                                                                                </Box>
                                                                            </Box>
                                                                        ),
                                                                    )}
                                                            </Stack>

                                                            {job.guestNotes && (
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        gap: 0.5,
                                                                        mt: 1,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        sx={{
                                                                            fontWeight:
                                                                                'bold',
                                                                            fontSize:
                                                                                '14px',
                                                                            mb: 0.5,
                                                                        }}
                                                                    >
                                                                        Notes:
                                                                    </Typography>
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize:
                                                                                '14px',
                                                                        }}
                                                                    >
                                                                        {
                                                                            job.guestNotes
                                                                        }
                                                                    </Typography>
                                                                </Box>
                                                            )}

                                                            <Box sx={{ mt: 1 }}>
                                                                <Divider />
                                                            </Box>

                                                            {job?.items && (
                                                                <Grid
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        justifyContent:
                                                                            'space-between',
                                                                        mt: 1,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            flex: 1,
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            sx={{
                                                                                fontWeight:
                                                                                    'bold',
                                                                                fontSize:
                                                                                    '14px',
                                                                            }}
                                                                        >
                                                                            Total
                                                                            (ex.
                                                                            GST)
                                                                        </Typography>
                                                                    </Box>
                                                                    <Typography
                                                                        sx={{
                                                                            fontWeight:
                                                                                'bold',
                                                                            fontSize:
                                                                                '14px',
                                                                        }}
                                                                    >
                                                                        {formatCurrency(
                                                                            sum(
                                                                                (
                                                                                    job.items as any[]
                                                                                ).map(
                                                                                    (
                                                                                        i: any,
                                                                                    ) =>
                                                                                        i.total,
                                                                                ),
                                                                            ),
                                                                        )}
                                                                    </Typography>
                                                                </Grid>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </Grid>

                                            <Grid item xs={12} md={4}>
                                                <Stack spacing={2}>
                                                    <JobMetaCard
                                                        job={job as any}
                                                        address={address}
                                                    />

                                                    {isContractorAdmin && (
                                                        <Box
                                                            sx={{
                                                                width: '100%',
                                                            }}
                                                        >
                                                            <AddToCalendarButton
                                                                disabled={
                                                                    !!job &&
                                                                    (job.status ===
                                                                        Status.Cancelled ||
                                                                        job.status ===
                                                                            Status.Completed ||
                                                                        job.status ===
                                                                            Status.Declined)
                                                                }
                                                                name={` ${job?.owner?.name} Job Request: #${job.id} - ${job.jobDate}`}
                                                                label='ADD TO CALENDAR'
                                                                iCalFileName={` ${job?.owner?.name} Job Request: #${job.id} - ${job.jobDate}`}
                                                                description={
                                                                    calendarDescription
                                                                }
                                                                options={[
                                                                    'iCal|Download',
                                                                    'Google',
                                                                    'Outlook.com',
                                                                    'Microsoft365',
                                                                ]}
                                                                location={
                                                                    address
                                                                }
                                                                startDate={
                                                                    job.jobDate as any
                                                                }
                                                                endDate={
                                                                    job.jobDate as any
                                                                }
                                                                timeZone='Australia/Sydney'
                                                                hideCheckmark
                                                                size='2'
                                                                listStyle='modal'
                                                                lightMode='bodyScheme'
                                                                styleLight={`--btn-background: ${theme.palette.neutral.main}; --btn-text: primary;`}
                                                                styleDark={`--btn-background: ${theme.palette.warning.main};`}
                                                            />
                                                        </Box>
                                                    )}

                                                    {isOwnerAdmin && (
                                                        <Button
                                                            variant='contained'
                                                            color='warning'
                                                            disabled={
                                                                !!job &&
                                                                (job.status ===
                                                                    Status.Cancelled ||
                                                                    job.status ===
                                                                        Status.Completed ||
                                                                    job.status ===
                                                                        Status.Declined)
                                                            }
                                                            onClick={
                                                                handleCancel
                                                            }
                                                        >
                                                            Cancel Job
                                                        </Button>
                                                    )}

                                                    {isOwnerAdmin &&
                                                        job.recurringId && (
                                                            <Button
                                                                variant='outlined'
                                                                color='primary'
                                                                onClick={
                                                                    handleViewRecurringJob
                                                                }
                                                            >
                                                                <EventRepeatIcon
                                                                    sx={{
                                                                        fontSize:
                                                                            '18px',
                                                                    }}
                                                                />
                                                                <span
                                                                    style={{
                                                                        marginLeft:
                                                                            '5px',
                                                                    }}
                                                                >
                                                                    View
                                                                    Recurring
                                                                    Job
                                                                </span>
                                                            </Button>
                                                        )}
                                                </Stack>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Tabs
                                                    value={tabPanel}
                                                    onChange={(_, v) =>
                                                        setTabPanel(v)
                                                    }
                                                    sx={{ pb: 1 }}
                                                >
                                                    <Tab
                                                        value={0}
                                                        label='Job Progress'
                                                    />
                                                    {isContractor &&
                                                        job.contractor
                                                            .permissions ===
                                                            'readAndWrite' && (
                                                            <Tab
                                                                value={1}
                                                                label='Your Notes'
                                                            />
                                                        )}
                                                    <Tab
                                                        value={2}
                                                        label='Messaging'
                                                    />
                                                    {job.module
                                                        .usesNirovision && (
                                                        <Tab
                                                            value={3}
                                                            label='Access'
                                                        />
                                                    )}
                                                </Tabs>

                                                <Stack
                                                    spacing={1}
                                                    display={
                                                        tabPanel === 0
                                                            ? 'default'
                                                            : 'none'
                                                    }
                                                >
                                                    <ProgressTab {...job} />
                                                </Stack>

                                                <Box
                                                    display={
                                                        tabPanel === 1
                                                            ? 'default'
                                                            : 'none'
                                                    }
                                                >
                                                    <NotesTab
                                                        {...job}
                                                        formOpen={notesFormOpen}
                                                        handleFormClose={() =>
                                                            setNotesFormOpen(
                                                                false,
                                                            )
                                                        }
                                                        handleFormOpen={() =>
                                                            setNotesFormOpen(
                                                                true,
                                                            )
                                                        }
                                                    />
                                                </Box>

                                                {!location.pathname.includes(
                                                    'messages',
                                                ) &&
                                                    job.module.messagingUsers
                                                        ?.length > 0 && (
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            display={
                                                                tabPanel === 2
                                                                    ? 'default'
                                                                    : 'none'
                                                            }
                                                        >
                                                            <ContactForm
                                                                {...job}
                                                            />
                                                        </Grid>
                                                    )}

                                                {job.module.usesNirovision && (
                                                    <Stack
                                                        spacing={1}
                                                        display={
                                                            tabPanel === 3
                                                                ? 'default'
                                                                : 'none'
                                                        }
                                                    >
                                                        <AccessTab {...job} />
                                                    </Stack>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </DialogContent>
                                </>
                            )}

                            <DialogActions
                                sx={{
                                    position: 'sticky',
                                    bottom: 0,
                                    zIndex: 1,
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Stack
                                    direction={isMobile ? 'column' : 'row'}
                                    spacing={1}
                                >
                                    {jobIds.length > 1 && (
                                        <Typography
                                            component='span'
                                            sx={{
                                                fontWeight: 400,
                                                fontSize: 14,
                                            }}
                                        >
                                            ({currentIndex + 1} of{' '}
                                            {jobIds.length} Jobs)
                                        </Typography>
                                    )}
                                </Stack>

                                <Stack direction='row' spacing={1}>
                                    {isContractorAdmin && (
                                        <>
                                            {job?.status === Status.Pending ? (
                                                <Button
                                                    variant='contained'
                                                    onClick={handleSubmit(
                                                        handleAccept,
                                                    )}
                                                >
                                                    Accept
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant='contained'
                                                    onClick={handleSubmit(
                                                        handleReassign,
                                                    )}
                                                    disabled={
                                                        !dirtyFields.assignee
                                                    }
                                                >
                                                    Save
                                                </Button>
                                            )}

                                            {(job?.status === Status.Pending ||
                                                job?.status ===
                                                    Status.Accepted) && (
                                                <Button
                                                    variant='contained'
                                                    color='error'
                                                    onClick={() =>
                                                        setDeclineOpen(true)
                                                    }
                                                >
                                                    Decline
                                                </Button>
                                            )}
                                        </>
                                    )}

                                    <Button
                                        onClick={handleClose}
                                        variant='outlined'
                                        color='primary'
                                        sx={{ flex: isMobile ? 1 : undefined }}
                                    >
                                        Close
                                    </Button>

                                    {isOwnerAdmin && (
                                        <Button
                                            variant='contained'
                                            color='primary'
                                            type='submit'
                                            disabled={!isDirty || isSubmitting}
                                        >
                                            Save
                                        </Button>
                                    )}
                                </Stack>
                            </DialogActions>
                        </form>
                    </FormProvider>
                )}
            </Dialog>

            <Lightbox
                open={lightboxOpen}
                index={selectedPhoto}
                slides={lightboxPhotos}
                close={() => setLightboxOpen(false)}
            />

            <DeclineModal
                job={job}
                open={declineOpen}
                onClose={() => setDeclineOpen(false)}
            />
        </>
    );
};

const PhotoUploadField = ({
    label,
    name,
    disabled,
    onUpload,
}: {
    label: string;
    name: 'jobCompletion.beforePhotos' | 'jobCompletion.afterPhotos';
    disabled: boolean;
    onUpload: (files: File[]) => Promise<any>;
}) => {
    const { control } = useFormContext<FormValues>();

    return (
        <FormField label={label}>
            <FormUpload
                name={name}
                control={control}
                hasLightBox
                onUpload={onUpload}
                disabled={disabled}
                validation={{
                    acceptedTypes: {
                        'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
                    },
                }}
            />
        </FormField>
    );
};

const ProgressTab = ({
    jobCompletion,
    status,
}: {
    jobCompletion: JobCompletion;
    status: Status;
}) => {
    const loggedInUser = UserRole();
    const isOwnerAdmin = loggedInUser?.role === OwnerRole.Admin;

    const { control, clearErrors, setError, setValue } =
        useFormContext<FormValues>();
    const [addPhotos] = useAddJobPhotosMutation();
    const values = useWatch({ control });

    const handleClearCompletion = () => {
        setValue('jobCompletion.notes', jobCompletion.notes);
        setValue('jobCompletion.afterPhotos', jobCompletion.afterPhotos as any);
        setValue(
            'jobCompletion.beforePhotos',
            jobCompletion.beforePhotos as any,
        );
    };

    const handlePhotos = async (
        files: File[],
        name: 'jobCompletion.beforePhotos' | 'jobCompletion.afterPhotos',
    ) => {
        try {
            const formData = new FormData();
            for (const image of files) formData.append('files', image);
            const response = await addPhotos(formData).unwrap();
            clearErrors(name);
            return response;
        } catch (error: any) {
            if (error.status === 400)
                setError(name, {
                    message: 'File size cannot exceed 5MB',
                } as any);
            else setError(name, { message: 'Error uploading files' } as any);
        }
    };

    return (
        <Card variant='outlined'>
            <CardContent>
                <Stack spacing={1}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                        Work Log
                    </Typography>

                    <FormField
                        label='Work Log Notes'
                        fieldId='contractor-job-work-log-notes'
                    >
                        <FormTextField
                            id='contractor-job-work-log-notes'
                            name='jobCompletion.notes'
                            control={control}
                            size='small'
                            fullWidth
                            multiline
                            rows={4}
                            disabled={isOwnerAdmin}
                        />
                    </FormField>

                    <PhotoUploadField
                        label='Before Photos'
                        name='jobCompletion.beforePhotos'
                        disabled={isOwnerAdmin}
                        onUpload={(files) =>
                            handlePhotos(files, 'jobCompletion.beforePhotos')
                        }
                    />

                    <PhotoUploadField
                        label='After Photos'
                        name='jobCompletion.afterPhotos'
                        disabled={isOwnerAdmin}
                        onUpload={(files) =>
                            handlePhotos(files, 'jobCompletion.afterPhotos')
                        }
                    />
                    <Collapse
                        in={!isEqual(values.jobCompletion, jobCompletion)}
                        unmountOnExit
                    >
                        <ActionButtons onCancel={handleClearCompletion} />
                    </Collapse>
                </Stack>
            </CardContent>
        </Card>
    );
};

const NotesTab = ({
    contractor,
    status,
    contractorNotes,
    formOpen,
    handleFormOpen,
    handleFormClose,
}: {
    contractor: Job['contractor'];
    status: Status;
    contractorNotes: string;
    formOpen: boolean;
    handleFormOpen: () => void;
    handleFormClose: () => void;
}) => {
    const { control, setValue, formState } = useFormContext<FormValues>();
    const { isDirty, isSubmitting } = formState;
    const loggedInUser = UserRole();
    const isContractor = loggedInUser?.role.includes('contractor');

    const handleClearNotes = () => {
        setValue('contractorNotes', contractorNotes);
        handleFormClose();
    };

    if (!isContractor || contractor.permissions !== 'readAndWrite') return null;

    return (
        <Card variant='outlined'>
            <CardContent>
                <Box>
                    <FieldLabel htmlFor='contractor-job-your-notes'>
                        Your Notes{' '}
                        <CustomTooltip
                            title={
                                <Typography>
                                    Only people from your company can see these
                                    notes.
                                </Typography>
                            }
                            placement='right'
                        >
                            <HelpOutline
                                sx={{ color: 'text.primary', fontSize: '16px' }}
                            />
                        </CustomTooltip>
                    </FieldLabel>

                    <FormTextField
                        id='contractor-job-your-notes'
                        name='contractorNotes'
                        control={control}
                        rows={4}
                        multiline={status === Status.Pending}
                        onFocus={handleFormOpen}
                    />

                    <FormHelperText>
                        Only people from your company can see these notes
                    </FormHelperText>
                </Box>

                <Collapse in={formOpen} unmountOnExit>
                    <Box sx={{ mt: 1 }}>
                        <ActionButtons
                            onCancel={handleClearNotes}
                            saveDisabled={!isDirty || isSubmitting}
                        />
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
};

type AccessResult = { ok: true } | { ok: false; error: string };

const AccessTab = ({
    id,
    status,
    jobDate,
    assignee,
    module,
}: {
    id: number;
    status: Status;
    jobDate: string;
    assignee: Job['assignee'] | null;
    module: Job['module'];
}) => {
    const {
        data: access,
        isLoading,
        refetch,
    } = useGetNirovisionJobAccessListQuery(id, { skip: !assignee });

    const [provideJobAccess] = useProvideNirovisionContractorAccessMutation();
    const [removeJobAccess] = useRemoveNirovisionContractorAccessMutation();

    const provideAccess = async (userId: number): Promise<AccessResult> => {
        try {
            await provideJobAccess({ jobId: id, userId }).unwrap();
            toast.success('Nirovision access granted successfully');
            return { ok: true as const };
        } catch (error) {
            const msg = handleHttpError(error);
            toast.error(msg);
            return { ok: false as const, error: msg };
        }
    };

    const removeAccess = async (userId: number): Promise<AccessResult> => {
        try {
            await removeJobAccess({ jobId: id, userId }).unwrap();
            toast.success('Nirovision access removed successfully');
            return { ok: true as const };
        } catch (error) {
            const msg = handleHttpError(error);
            toast.error(msg);
            return { ok: false as const, error: msg };
        }
    };

    return (
        <Card variant='outlined'>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                        Nirovision Access
                    </Typography>
                    <CustomTooltip title='Refresh Access'>
                        <IconButton
                            size='small'
                            color='primary'
                            onClick={refetch}
                        >
                            <Refresh fontSize='small' />
                        </IconButton>
                    </CustomTooltip>
                </Box>

                {!access && !isLoading && (
                    <Alert severity='error'>Error fetching access data</Alert>
                )}

                <NirovisionAccessTable
                    data={access}
                    status={status}
                    start={jobDate}
                    end={jobDate}
                    usesVehicleRec={module.usesVehicleRec}
                    provideAccess={provideAccess}
                    removeAccess={removeAccess}
                    accessType='job'
                />
            </CardContent>
        </Card>
    );
};

const CompleteJobAlert = ({ id }: { id: number }) => {
    const [showMarkAsCompletedModal, setShowMarkAsCompletedModal] =
        useState(false);

    const [completeJob] = useCompleteJobMutation();

    const handleComplete = async () => {
        try {
            await completeJob(id).unwrap();
            toast.success('Job marked as complete');
        } catch {
            toast.error('Error marking job complete');
        }
    };

    return (
        <>
            <Alert
                severity='info'
                action={
                    <Button
                        variant='outlined'
                        size='small'
                        onClick={() => setShowMarkAsCompletedModal(true)}
                        endIcon={<CheckCircle />}
                    >
                        Mark as Complete
                    </Button>
                }
                sx={{ mb: 2 }}
            >
                Has this job been completed?
            </Alert>
            <MarkAsCompletedModal
                open={showMarkAsCompletedModal}
                onClose={() => setShowMarkAsCompletedModal(false)}
                onSubmit={handleComplete}
            />
        </>
    );
};

export default ContractorJobPreview;
